import { useCallback } from 'react';
import { toast } from 'sonner';

import { messageOf, sayOutcome } from './commandOutcome';
import { useSetTaskHidden } from './useTaskLifecycle';

/**
 * Taking a task off the board, with the way back offered (spec 125 FR-011).
 *
 * One behaviour for the board's card menu and the Tasks page's row actions,
 * which used to differ: the Tasks page toasted with Undo, the board removed the
 * card silently. The toast names the task, because the card it describes has
 * just disappeared from view.
 *
 * Said through `sayOutcome`: hiding unmounts the menu that asked, and
 * callbacks handed to `mutate` would be dropped with it (spec 151 FR-001).
 */
export function useHideTask(workspaceId: string) {
  const setHidden = useSetTaskHidden();

  /** Back on the board: the way back from a hide, and an action of its own. */
  const show = useCallback(
    (task: { id: string; title: string }) => {
      sayOutcome(
        setHidden.mutateAsync({ workspaceId, taskId: task.id, hidden: false }),
        {
          accepted: () => {
            toast(`${task.title} is back on the board`);
          },
          failed: (error) => {
            toast.error(messageOf(error));
          },
        },
      );
    },
    [setHidden, workspaceId],
  );

  const setTaskHidden = useCallback(
    (task: { id: string; title: string }, hidden: boolean) => {
      if (!hidden) {
        show(task);
        return;
      }
      sayOutcome(
        setHidden.mutateAsync({ workspaceId, taskId: task.id, hidden: true }),
        {
          accepted: () => {
            toast(`Hidden ${task.title}`, {
              action: {
                label: 'Undo',
                onClick: () => {
                  show(task);
                },
              },
            });
          },
          failed: (error) => {
            toast.error(messageOf(error));
          },
        },
      );
    },
    [setHidden, show, workspaceId],
  );

  return { setTaskHidden, isPending: setHidden.isPending };
}

/** Why Hide is unavailable, in the words the menu shows (FR-012). */
export const HIDE_WHILE_RUNNING_REASON = 'Stop the run first';
